"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFieldRanges = exports.getFillHandleStatus = exports.getSelectedRecord = exports.getSelectedField = exports.getCellUIIndex = exports.getCellByIndex = exports.getCellIndex = exports.isCellInSelection = exports.getSelectRecordIds = exports.isColumnSpaceEnough = exports.isRowSpaceEnough = exports.getRangeRows = exports.getStdValueTableFromRange = exports.getCellMatrixFromRange = exports.getCellMatrixFromSelection = exports.getSelectionRecordRanges = exports.getSelectRanges = exports.getSelection = exports.isCellVisible = exports.getRangeRecords = void 0;
const lodash_1 = require("lodash");
const model_1 = require("../../../../../../model");
const view_1 = require("../../../../../../model/view");
const constants_1 = require("../../../../../../modules/shared/store/constants");
const reselect_1 = require("reselect");
const base_1 = require("./base");
const calc_1 = require("./calc");
const rows_calc_1 = require("./rows_calc");
const cell_calc_1 = require("./cell_calc");
/**
 *
 * Field class needs to depend on the root state to calculate,
 * we need to pass the root state to the selector,
 * but we don't want to break the selector cache.
 *
 * consider most cases, memorize the root state as a parameter is meaningless,
 * so we make an assumption here,
 * if the user passes the root state as a parameter,
 * then the memoize function never checks its change.
 */
const createSelectorIgnoreState = (0, reselect_1.createSelectorCreator)(reselect_1.defaultMemoize, (pre, next) => {
    // if compare to root state, always return true
    if ((0, lodash_1.isObject)(pre) && (0, lodash_1.isObject)(next) && 'isStateRoot' in pre && 'isStateRoot' in next) {
        return true;
    }
    // common reference comparison
    return pre === next;
});
// TODO: memory special attention
const getStdValueMatrixFromIds = (state, snapshot, ids) => {
    const { rows, columns } = ids;
    return rows.map(row => {
        const recordId = row.recordId;
        return columns.map(column => {
            const fieldId = column.fieldId;
            const field = snapshot.meta.fieldMap[fieldId];
            const cellValue = (0, cell_calc_1.getCellValue)(state, snapshot, recordId, fieldId);
            return model_1.Field.bindContext(field, state).cellValueToStdValue(cellValue);
        });
    });
};
const getCellMatrix = (rows, columns) => {
    return rows.map(row => {
        const recordId = row.recordId;
        return columns.map(column => {
            const fieldId = column.fieldId;
            return { recordId, fieldId };
        });
    });
};
const getRangeRecords = (state, range) => {
    const rangeIndex = view_1.Range.bindModel(range).getIndexRange(state);
    if (!rangeIndex) {
        return null;
    }
    const rowSlice = [rangeIndex.record.min, rangeIndex.record.max + 1];
    const rows = (0, rows_calc_1.getVisibleRows)(state);
    return rows.slice(...rowSlice);
};
exports.getRangeRecords = getRangeRecords;
const isCellVisible = (state, cell) => {
    const visibleRowIndexMap = (0, rows_calc_1.getVisibleRowsIndexMap)(state);
    const visibleColumnIndexMap = (0, calc_1.getVisibleColumnsMap)(state);
    return visibleRowIndexMap.has(cell.recordId) && visibleColumnIndexMap.has(cell.fieldId);
};
exports.isCellVisible = isCellVisible;
const getSelection = (state) => {
    const client = (0, base_1.getDatasheetClient)(state);
    const selection = client && client.selection;
    // whether activeCell move out
    if (selection && selection.activeCell && !(0, exports.isCellVisible)(state, selection.activeCell)) {
        return null;
    }
    // the start of the selection area and the end of the selection area are removed
    if (selection && selection.ranges) {
        const { start, end } = selection.ranges[0];
        if (!(0, exports.isCellVisible)(state, start) || !(0, exports.isCellVisible)(state, end)) {
            return null;
        }
    }
    return selection;
};
exports.getSelection = getSelection;
exports.getSelectRanges = (0, reselect_1.createSelector)([exports.getSelection], selection => {
    if (!selection || !selection.ranges) {
        return [];
    }
    return selection.ranges;
});
exports.getSelectionRecordRanges = (0, reselect_1.createSelector)([exports.getSelection], selection => {
    return selection ? selection.recordRanges : undefined;
});
/**
 * from sequential or non-sequential selection area, get selected cells 2d array.
 * @param state
 */
const getCellMatrixFromSelection = (state) => {
    const selectionRanges = (0, exports.getSelectRanges)(state);
    const selectionRecordRanges = (0, exports.getSelectionRecordRanges)(state);
    // non-sequence selection
    if (selectionRecordRanges) {
        const visibleColumns = (0, calc_1.getVisibleColumns)(state);
        return selectionRecordRanges.map(recordId => {
            return visibleColumns.map(column => {
                return {
                    recordId,
                    fieldId: column.fieldId,
                };
            });
        });
    }
    // sequence selection
    if (!selectionRanges.length) {
        return null;
    }
    return (0, exports.getCellMatrixFromRange)(state, selectionRanges[0]);
};
exports.getCellMatrixFromSelection = getCellMatrixFromSelection;
const getCellMatrixFromRange = (state, range) => {
    const datasheet = (0, base_1.getDatasheet)(state);
    const snapshot = datasheet && datasheet.snapshot;
    if (!snapshot) {
        return null;
    }
    const view = (0, calc_1.getCurrentView)(state);
    if (!view) {
        return null;
    }
    const rangeIndex = view_1.Range.bindModel(range).getIndexRange(state);
    if (!rangeIndex) {
        return null;
    }
    const { record, field } = rangeIndex;
    const rowSlice = [record.min, record.max + 1];
    const columnSlice = [field.min, field.max + 1];
    const rows = (0, rows_calc_1.getVisibleRows)(state).slice(...rowSlice);
    const columns = (0, calc_1.getVisibleColumns)(state).slice(...columnSlice);
    return getCellMatrix(rows, columns);
};
exports.getCellMatrixFromRange = getCellMatrixFromRange;
const getStdValueTableFromRange = (state, range) => {
    const datasheet = (0, base_1.getDatasheet)(state);
    const snapshot = datasheet && datasheet.snapshot;
    if (!snapshot) {
        return null;
    }
    const view = (0, calc_1.getCurrentView)(state);
    if (!view) {
        return null;
    }
    const indexRange = view_1.Range.bindModel(range).getIndexRange(state);
    if (!indexRange) {
        return null;
    }
    const { record, field } = indexRange;
    const rowSlice = [record.min, record.max + 1];
    const columnSlice = [field.min, field.max + 1];
    const rows = (0, rows_calc_1.getVisibleRows)(state).slice(...rowSlice);
    const columns = (0, calc_1.getVisibleColumns)(state).slice(...columnSlice);
    const stdValueMatrix = getStdValueMatrixFromIds(state, snapshot, { rows, columns });
    const fieldDataArr = columns.map(column => snapshot.meta.fieldMap[column.fieldId]);
    return {
        datasheetId: state.pageParams.datasheetId,
        viewId: view.id,
        header: fieldDataArr,
        body: stdValueMatrix,
        recordIds: rows.map(row => row.recordId),
    };
};
exports.getStdValueTableFromRange = getStdValueTableFromRange;
const getRangeRows = (state, start, end) => {
    const view = (0, calc_1.getCurrentView)(state);
    if (!view) {
        return [];
    }
    const rows = (0, rows_calc_1.getVisibleRows)(state);
    const groupInfo = (0, calc_1.getActiveViewGroupInfo)(state);
    if (groupInfo.length) {
        const groupSketch = new view_1.Group(groupInfo, (0, rows_calc_1.getGroupBreakpoint)(state));
        const depthBreakpoints = groupSketch.getDepthGroupBreakPoints();
        const curBreakpoint = depthBreakpoints.find(bp => bp > start);
        return rows.slice(start, curBreakpoint ? Math.min(curBreakpoint, end) : end);
    }
    return rows.slice(start, end);
};
exports.getRangeRows = getRangeRows;
const isRowSpaceEnough = (state, length, startRowIndex) => {
    // consider the grouping situation, we need to know whether the current grouping has enough space to paste data
    const rowLength = (0, rows_calc_1.getVisibleRows)(state).length;
    if (rowLength <= 0) {
        return false;
    }
    return length <= (0, exports.getRangeRows)(state, startRowIndex, rowLength).length;
};
exports.isRowSpaceEnough = isRowSpaceEnough;
const isColumnSpaceEnough = (state, length, activeCol) => {
    const columnLength = (0, calc_1.getVisibleColumnCount)(state);
    return length <= columnLength - activeCol;
};
exports.isColumnSpaceEnough = isColumnSpaceEnough;
/**
 * get selected records collection, no matter by checkbox or range selection
 * @param state
 */
exports.getSelectRecordIds = createSelectorIgnoreState([state => state, exports.getSelectRanges, exports.getSelectionRecordRanges], (state, ranges, checkedRecordIds) => {
    const range = ranges[0];
    // if selection area exists, return the selected records in the area
    if (range) {
        const rangeRecords = (0, exports.getRangeRecords)(state, range);
        return rangeRecords ? rangeRecords.map(row => row.recordId) : [];
    }
    // otherwise return the checked records
    return checkedRecordIds || [];
});
const isCellInSelection = (state, cell) => {
    const selection = (0, exports.getSelection)(state);
    if (!selection) {
        return false;
    }
    if (!selection.ranges) {
        const selectedRecordIds = (0, exports.getSelectRecordIds)(state);
        const inSelectRecords = new Set(selectedRecordIds).has(cell.recordId);
        return inSelectRecords;
    }
    return selection.ranges.some(range => {
        return view_1.Range.bindModel(range).contains(state, cell);
    });
};
exports.isCellInSelection = isCellInSelection;
const getCellIndex = (state, cell) => {
    const visibleRowIndexMap = (0, rows_calc_1.getVisibleRowsIndexMap)(state);
    const visibleColumnIndexMap = (0, calc_1.getVisibleColumnsMap)(state);
    if ((0, exports.isCellVisible)(state, cell)) {
        return {
            recordIndex: visibleRowIndexMap.get(cell.recordId),
            fieldIndex: visibleColumnIndexMap.get(cell.fieldId),
        };
    }
    return null;
};
exports.getCellIndex = getCellIndex;
const getCellByIndex = (state, cellIndex) => {
    const { recordIndex, fieldIndex } = cellIndex;
    const visibleRows = (0, rows_calc_1.getVisibleRows)(state);
    const visibleColumns = (0, calc_1.getVisibleColumns)(state);
    const cell = {
        recordId: visibleRows[recordIndex].recordId,
        fieldId: visibleColumns[fieldIndex].fieldId,
    };
    if ((0, exports.isCellVisible)(state, cell)) {
        return cell;
    }
    return null;
};
exports.getCellByIndex = getCellByIndex;
const getCellUIIndex = (state, cell) => {
    const visibleColumnIndexMap = (0, calc_1.getVisibleColumnsMap)(state);
    const linearRowIndexMap = (0, rows_calc_1.getLinearRowsIndexMap)(state);
    if ((0, exports.isCellVisible)(state, cell)) {
        return {
            rowIndex: linearRowIndexMap.get(`${constants_1.CellType.Record}_${cell.recordId}`),
            columnIndex: visibleColumnIndexMap.get(cell.fieldId),
        };
    }
    return null;
};
exports.getCellUIIndex = getCellUIIndex;
const getSelectedField = (state) => {
    const selection = (0, exports.getSelection)(state);
    const datasheet = (0, base_1.getDatasheet)(state);
    if (!selection || !datasheet || !selection.activeCell) {
        return;
    }
    const fieldId = selection.activeCell.fieldId;
    return datasheet.snapshot.meta.fieldMap[fieldId];
};
exports.getSelectedField = getSelectedField;
const getSelectedRecord = (state) => {
    const selection = (0, exports.getSelection)(state);
    const datasheet = (0, base_1.getDatasheet)(state);
    if (!selection || !datasheet || !selection.activeCell) {
        return;
    }
    const recordId = selection.activeCell.recordId;
    return datasheet.snapshot.recordMap[recordId];
};
exports.getSelectedRecord = getSelectedRecord;
const getFillHandleStatus = (state) => {
    const selection = (0, exports.getSelection)(state);
    return selection === null || selection === void 0 ? void 0 : selection.fillHandleStatus;
};
exports.getFillHandleStatus = getFillHandleStatus;
const getFieldRanges = (state) => {
    var _a;
    return (_a = (0, exports.getSelection)(state)) === null || _a === void 0 ? void 0 : _a.fieldRanges;
};
exports.getFieldRanges = getFieldRanges;
//# sourceMappingURL=cell_range_calc.js.map